/**
 * Client Authentication Context
 * Provides client auth state (separate from Admin OAuth).
 * Uses ayu_client_session cookie via tRPC clientAuth.me query.
 */
import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
} from "react";
import { trpc } from "@/lib/trpc";

export interface ClientUser {
  id: number;
  clientId: string;
  email: string;
  firstName: string;
  lastName: string;
  packageType: "basis" | "premium";
  onboardingComplete: boolean;
  coachingStyle: string | null;
  doshaProfile: unknown;
  anamneseData: unknown;
  foodPreferences: unknown;
  birthdate: string | null;
  voicePreference: "annam" | "christopher";
  language: "de" | "en";
  isActive: boolean;
  isAdmin: boolean;
  avatarUrl: string | null;
  createdAt: Date;
}

interface ClientAuthContextType {
  client: ClientUser | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  login: (
    email: string,
    password: string
  ) => Promise<{ success: boolean; onboardingComplete?: boolean }>;
  register: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }) => Promise<{ success: boolean }>;
  logout: () => Promise<void>;
  refetch: () => void;
}

const ClientAuthContext = createContext<ClientAuthContextType | undefined>(
  undefined
);

export function ClientAuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [error, setError] = useState<string | null>(null);

  const meQuery = trpc.clientAuth.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
  });

  const loginMutation = trpc.clientAuth.login.useMutation();
  const registerMutation = trpc.clientAuth.register.useMutation();
  const logoutMutation = trpc.clientAuth.logout.useMutation();
  const utils = trpc.useUtils();

  const login = useCallback(
    async (email: string, password: string) => {
      setError(null);
      try {
        const result = await loginMutation.mutateAsync({ email, password });
        // Check if the server returned success: false (invalid credentials)
        if (!result.success) {
          const msg = result.error || "Login fehlgeschlagen";
          setError(msg);
          return { success: false };
        }
        // Refetch me query and wait for it to resolve with fresh data
        await utils.clientAuth.me.refetch();
        return {
          success: true,
          onboardingComplete: result.client?.onboardingComplete ?? false,
        };
      } catch (err: any) {
        const msg = err?.message || "Login fehlgeschlagen";
        setError(msg);
        return { success: false };
      }
    },
    [loginMutation, utils]
  );

  const register = useCallback(
    async (data: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
    }) => {
      setError(null);
      try {
        const result = await registerMutation.mutateAsync(data);
        // Check if the server returned success: false (e.g. email already taken)
        if (!result.success) {
          const msg = result.error || "Registrierung fehlgeschlagen";
          setError(msg);
          return { success: false };
        }
        // Refetch me query and wait for it to resolve with fresh data
        await utils.clientAuth.me.refetch();
        return { success: true };
      } catch (err: any) {
        const msg = err?.message || "Registrierung fehlgeschlagen";
        setError(msg);
        return { success: false };
      }
    },
    [registerMutation, utils]
  );

  const logout = useCallback(async () => {
    try {
      await logoutMutation.mutateAsync();
      utils.clientAuth.me.setData(undefined, null);
    } catch {
      // ignore
    }
  }, [logoutMutation, utils]);

  const refetch = useCallback(() => {
    utils.clientAuth.me.invalidate();
  }, [utils]);

  const value: ClientAuthContextType = {
    client: meQuery.data ?? null,
    loading: meQuery.isLoading,
    error,
    isAuthenticated: !!meQuery.data,
    login,
    register,
    logout,
    refetch,
  };

  return (
    <ClientAuthContext.Provider value={value}>
      {children}
    </ClientAuthContext.Provider>
  );
}

export function useClientAuth() {
  const context = useContext(ClientAuthContext);
  if (!context) {
    throw new Error("useClientAuth must be used within a ClientAuthProvider");
  }
  return context;
}
